import { useState } from 'react';
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  HStack,
  Heading,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Avatar, usePicture } from '../Avatar';
import { useAtomValue } from 'jotai';
import type { ChatParticipant } from './ConversationDialogs';
import { memberLabelsAtom } from '../../store';
import { useT } from '../../i18n';

export interface ProfilePhotoPerson {
  /** Whose picture it is, to open it (D294). */
  id?: string;
  displayName: string;
  handle?: string | null;
  avatarUrl?: string | null;
}

/**
 * Someone's profile picture, big (D279, issue #88): the picture filling the
 * width up to 360 px, or their initials when they have none, with their name.
 */
export function ProfilePhotoViewer({
  person,
  familyId,
  onClose,
}: {
  person: ProfilePhotoPerson | null;
  /** The chat's family: a face opens only through it (D292). */
  familyId: string | null | undefined;
  onClose: () => void;
}) {
  const t = useT();
  const photo = usePicture(person?.avatarUrl, {
    ownerId: person?.id,
    familyId,
  });
  if (!person) return null;
  const name = person.displayName;
  return (
    <Modal isOpen onClose={onClose} isCentered size="sm">
      <ModalOverlay bg="rgba(31, 29, 27, 0.85)" />
      <ModalContent
        mx={5}
        bg="transparent"
        boxShadow="none"
        aria-label={t('chat.people.photoOf', { name })}
      >
        <ModalCloseButton color="white" top="-44px" right={0} />
        <ModalBody p={0}>
          <VStack spacing={4}>
            {photo ? (
              <Image
                src={photo}
                alt={t('chat.people.photoOf', { name })}
                w="full"
                maxW="360px"
                aspectRatio={1}
                objectFit="cover"
                borderRadius="24px"
                data-testid="profile-photo-large"
              />
            ) : (
              <Avatar
                name={name}
                w="200px"
                h="200px"
                sx={{ '--avatar-font-size': '80px' }}
                fontFamily="heading"
                fontWeight={800}
                data-testid="profile-photo-initials"
              />
            )}
            <Box textAlign="center" color="white">
              <Text fontFamily="heading" fontWeight={800} fontSize="22px">
                {name}
              </Text>
              {person.handle && (
                <Text fontSize="15px" opacity={0.8}>
                  @{person.handle}
                </Text>
              )}
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

/**
 * Who's in this chat (D279, issue #88), from the faces under its name: each
 * person with their picture; tap a picture to see it big. Anyone in the
 * chat can open it, unlike group info (D234).
 */
export function PeopleSheet({
  isOpen,
  participants,
  currentUserId,
  familyId,
  onClose,
}: {
  isOpen: boolean;
  participants: ChatParticipant[];
  currentUserId: string | undefined;
  /** The chat's family, to open faces through (D292). */
  familyId: string | null | undefined;
  onClose: () => void;
}) {
  const t = useT();
  const memberLabels = useAtomValue(memberLabelsAtom);
  const phone = useBreakpointValue({ base: true, md: false }) ?? true;
  const [shown, setShown] = useState<ProfilePhotoPerson | null>(null);
  if (!isOpen) return null;

  const content = (
    <VStack align="stretch" spacing={2}>
      <Heading as="h2" fontSize="22px" pb={1}>
        {t('chat.people.title', { n: participants.length })}
      </Heading>
      {participants.map((participant) => {
        const name = memberLabels[participant.id] ?? participant.displayName;
        const mine = participant.id === currentUserId;
        return (
          <HStack key={participant.id} spacing={3} minH="56px">
            <Box
              as="button"
              type="button"
              borderRadius="full"
              aria-label={t('chat.people.seePhoto', { name })}
              onClick={() => setShown({ ...participant, displayName: name })}
              data-testid="people-photo"
            >
              <Avatar
                size="md"
                name={participant.displayName}
                src={participant.avatarUrl ?? undefined}
                ownerId={participant.id}
                familyId={familyId}
              />
            </Box>
            <Box flex="1" minW={0}>
              <Text fontSize="16px" fontWeight={600} noOfLines={1}>
                {mine ? `${name} ${t('chat.dialogs.youSuffix')}` : name}
              </Text>
              <Text fontSize="14px" color="muted" noOfLines={1}>
                @{participant.handle}
              </Text>
            </Box>
          </HStack>
        );
      })}
    </VStack>
  );

  return (
    <>
      {phone ? (
        <Drawer isOpen placement="bottom" onClose={onClose}>
          <DrawerOverlay bg="rgba(31, 29, 27, 0.5)" />
          <DrawerContent
            borderTopRadius="28px"
            bg="surface"
            maxH="92dvh"
            pb="max(env(safe-area-inset-bottom), 16px)"
          >
            <DrawerBody px={5} pt="10px" pb={2} overflowY="auto">
              <Box
                w="40px"
                h="5px"
                borderRadius="full"
                bg="line"
                mx="auto"
                mb={4}
              />
              {content}
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      ) : (
        <Modal isOpen onClose={onClose} isCentered size="md">
          <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
          <ModalContent mx={5} borderRadius="28px" bg="surface">
            <ModalCloseButton />
            <ModalBody px={5} py={6}>
              {content}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
      <ProfilePhotoViewer
        person={shown}
        familyId={familyId}
        onClose={() => setShown(null)}
      />
    </>
  );
}
